// src/components/ReviewSection.jsx
// COMPLETE FIX: All fetching, displaying, and state management issues resolved

import React, { useState, useEffect } from 'react';
import { Star, Send, Trash2, Edit2, User, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { useUser } from '../contexts/UserContext';
import './css/ReviewSection.css';

const ReviewSection = ({ product, onProductUpdate }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [error, setError] = useState('');
  const [userReview, setUserReview] = useState(null);

  // SAFE: Convert averageRating to number
  const averageRating = product.averageRating != null ? Number(product.averageRating) : null;
  const ratingDisplay = averageRating !== null && !isNaN(averageRating)
    ? averageRating.toFixed(1)
    : 'N/D';

  useEffect(() => {
    fetchReviews();
  }, [product.id, pagination.page]);

  useEffect(() => {
    // Find user's existing review
    if (user && reviews.length > 0) {
      const existingReview = reviews.find(r => r.userId === user.id);
      setUserReview(existingReview || null);
    } else {
      setUserReview(null);
    }
  }, [user, reviews]);

  const fetchReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await productsAPI.getReviews(product.id, pagination.page);
      setReviews(data.reviews || []);
      setPagination(data.pagination || { page: 1, limit: 10, total: 0, pages: 1 });
    } catch (err) {
      console.error('Failed to load reviews:', err);
      setError('Impossible de charger les avis');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshProductData = async () => {
    try {
      const { data } = await productsAPI.getById(product.id);
      if (onProductUpdate) {
        onProductUpdate(data);
      }
    } catch (err) {
      console.error('Failed to refresh product data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      alert('Veuillez vous connecter pour soumettre un avis');
      navigate('/login');
      return;
    }

    if (!rating || rating < 1 || rating > 5) {
      alert('Veuillez choisir une note entre 1 et 5 etoiles');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await productsAPI.submitReview(product.id, { 
        rating, 
        comment: comment.trim() || null 
      });

      // Reset form
      setRating(5);
      setComment('');
      setEditingReview(null);

      // Refresh reviews and product data
      await Promise.all([
        fetchReviews(),
        refreshProductData()
      ]);

      alert(editingReview ? 'Avis mis a jour avec succes !' : 'Avis envoye avec succes !');
    } catch (err) {
      console.error('Failed to submit review:', err);
      const errorMsg = err.response?.data?.message || "Impossible d'envoyer l'avis. Veuillez reessayer.";
      setError(errorMsg);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer votre avis ?')) return;
    
    setError('');
    try {
      await productsAPI.deleteReview(product.id);
      
      // Reset form
      setRating(5);
      setComment('');
      setEditingReview(null);
      setUserReview(null);

      // Refresh reviews and product data
      await Promise.all([
        fetchReviews(),
        refreshProductData()
      ]);

      alert('Avis supprime avec succes !');
    } catch (err) {
      console.error('Failed to delete review:', err);
      const errorMsg = err.response?.data?.message || "Impossible de supprimer l'avis. Veuillez reessayer.";
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setRating(review.rating);
    setComment(review.comment || '');
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingReview(null);
    setRating(5);
    setComment('');
  };

  const getStarFill = (starIndex) => {
    return starIndex < rating ? '#FFD700' : '#e5e7eb';
  };

  return (
    <div className="reviewsection-container">
      <div className="reviewsection-header">
        <h2 className="reviewsection-title">Avis clients</h2>
        <div className="reviewsection-summary">
          <div className="reviewsection-rating">
            <Star size={24} fill="#FFD700" color="#FFD700" />
            <span className="reviewsection-average">{ratingDisplay}</span>
          </div>
          <span className="reviewsection-total">
            {product.totalReviews || 0} avis
          </span>
        </div>
      </div>

      {/* Submit Review Form - Only for logged in users */}
      {user ? (
        <form onSubmit={handleSubmit} className="reviewsection-form">
          <h3 className="reviewsection-form-title">
            {editingReview ? 'Modifier votre avis' : userReview ? 'Mettre a jour votre avis' : 'Ecrire un avis'}
          </h3>
          <div className="reviewsection-rating-input">
            <span>Votre note :</span>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="reviewsection-star-btn"
                aria-label={`Noter ${star} etoiles`}
              >
                <Star size={28} fill={getStarFill(star)} color={getStarFill(star)} />
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Partagez votre experience avec ce produit... (optionnel)"
            className="reviewsection-textarea"
            rows="4"
            maxLength="1000"
          />
          <div className="reviewsection-form-actions">
            <button
              type="submit"
              disabled={submitting || !rating}
              className="reviewsection-submit-btn"
            >
              {submitting ? 'Envoi...' : editingReview || userReview ? 'Mettre a jour l\'avis' : 'Envoyer l\'avis'}
              <Send size={16} />
            </button>
            {editingReview && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="reviewsection-cancel-btn"
              >
                Annuler
              </button>
            )}
            {userReview && !editingReview && (
              <button
                type="button"
                onClick={handleDelete}
                className="reviewsection-delete-btn"
                title="Supprimer votre avis"
              >
                <Trash2 size={16} />
                Supprimer l'avis
              </button>
            )}
          </div>
          {error && <p className="reviewsection-error">{error}</p>}
        </form>
      ) : (
        // Guest user - Show login prompt
        <div className="reviewsection-guest-prompt">
          <div className="reviewsection-guest-content">
            <LogIn size={48} className="reviewsection-guest-icon" />
            <h3>Vous voulez partager votre experience ?</h3>
            <p>Creez un compte pour noter et commenter ce produit</p>
            <button
              onClick={() => navigate('/register')}
              className="reviewsection-signup-btn"
            >
              Creer un compte
            </button>
            <button
              onClick={() => navigate('/login')}
              className="reviewsection-login-btn"
            >
              Vous avez deja un compte ? Connexion
            </button>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="reviewsection-list">
        <h3 className="reviewsection-list-title">
          {reviews.length > 0 ? 'Ce que disent nos clients' : 'Avis clients'}
        </h3>
        
        {loading && <p className="reviewsection-loading">Chargement des avis...</p>}
        
        {!loading && reviews.length === 0 && (
          <p className="reviewsection-empty">
            Aucun avis pour le moment. {user ? 'Soyez le premier a donner votre avis !' : 'Connectez-vous pour laisser le premier avis !'}
          </p>
        )}
        
        {!loading && reviews.map((review) => {
          const isUserReview = user && user.id === review.userId;
          return (
            <div key={review.id} className={`reviewsection-review ${isUserReview ? 'reviewsection-user-review' : ''}`}>
              <div className="reviewsection-review-header">
                <div className="reviewsection-user">
                  <div className="reviewsection-avatar">
                    <User size={20} />
                  </div>
                  <div>
                    <p className="reviewsection-username">
                      {review.firstName} {review.lastName?.charAt(0)}.
                      {isUserReview && <span className="reviewsection-you-badge"> (Vous)</span>}
                    </p>
                    <p className="reviewsection-date">
                      {new Date(review.createdAt).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="reviewsection-review-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={16}
                      fill={star <= review.rating ? '#FFD700' : '#e5e7eb'}
                      color={star <= review.rating ? '#FFD700' : '#e5e7eb'}
                    />
                  ))}
                </div>
              </div>
              {review.comment && (
                <p className="reviewsection-comment">{review.comment}</p>
              )}
              {isUserReview && (
                <div className="reviewsection-actions">
                  <button
                    onClick={() => handleEdit(review)}
                    className="reviewsection-edit-btn"
                    title="Modifier votre avis"
                  >
                    <Edit2 size={14} />
                    Modifier
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {!loading && pagination.pages > 1 && (
        <div className="reviewsection-pagination">
          <button
            onClick={() => setPagination({ ...pagination, page: Math.max(1, pagination.page - 1) })}
            disabled={pagination.page === 1}
            className="reviewsection-page-btn"
          >
            Precedent
          </button>
          <span className="reviewsection-page-info">
            Page {pagination.page} sur {pagination.pages}
          </span>
          <button
            onClick={() => setPagination({ ...pagination, page: Math.min(pagination.pages, pagination.page + 1) })}
            disabled={pagination.page === pagination.pages}
            className="reviewsection-page-btn"
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
